/**
 * Test Data Generator — tworzy testową mapę z 2 seedami × 3 nody każdy
 * + encje + EAV + relacje. Używany do quick smoke testu pipeline UI.
 *
 * Wszystkie wpisy mają prefix "TEST_" w nazwie żeby łatwo odfiltrować/usunąć.
 * Działa przez supabase client (anon key + RLS is_allowed_user()).
 */

import { supabase } from "@/integrations/supabase/client";
import { uiLog } from "@/services/contentEngine/contentLogger";

export interface TestMapResult {
  mapId: string;
  mapName: string;
  seedCount: number;
  nodeCount: number;
  entityCount: number;
  eavCount: number;
  relationCount: number;
}

const SEED_TEMPLATES = [
  {
    name: "TEST: Frontend Frameworks",
    slug: "frontend-frameworks",
    seed_type: "core",
    description: "Comparison of React, Vue, Svelte for SPA development",
    nodes: [
      { title: "What is React?", role: "definition", entity: "React", attribute: "definition" },
      { title: "React vs Vue performance benchmark", role: "comparison", entity: "React", attribute: "vs Vue" },
      { title: "How to start a React project", role: "how_to", entity: "React", attribute: "setup" },
    ],
  },
  {
    name: "TEST: State Management",
    slug: "state-management",
    seed_type: "outer",
    description: "Redux, Zustand, Jotai patterns for React state",
    nodes: [
      { title: "What is Redux Toolkit?", role: "definition", entity: "Redux Toolkit", attribute: "definition" },
      { title: "Zustand vs Redux comparison", role: "comparison", entity: "Zustand", attribute: "vs Redux" },
      { title: "How to migrate from Redux to Zustand", role: "how_to", entity: "Zustand", attribute: "migration" },
    ],
  },
];

export async function generateTestMap(): Promise<TestMapResult> {
  uiLog.public("TestGen", "Start generowania mapy testowej");

  const { data: userData } = await supabase.auth.getUser();
  const userId = userData.user?.id;
  if (!userId) throw new Error("Brak zalogowanego użytkownika");

  const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
  const mapName = `TEST_Map_${timestamp}`;

  // 1. Create map
  const { data: mapRow, error: mapErr } = await (supabase as any)
    .from("thematic_maps")
    .insert({
      user_id: userId,
      name: mapName,
      status: "draft",
      language: "pl",
      map_version: 1,
      macro_statement: "Testowa mapa pipeline UX (auto-wygenerowana). Zawiera 2 seedy × 3 nody.",
      scope_in: ["test scope a", "test scope b", "test scope c"],
      scope_out: ["wykluczony test"],
      audience: "test users",
      boundaries_owned: ["frontend frameworks", "state management"],
      boundaries_not_owned: ["backend frameworks"],
      boundaries_adjacent: ["build tools"],
    })
    .select()
    .single();

  if (mapErr) throw mapErr;
  const mapId = mapRow.id;
  uiLog.public("TestGen", `Mapa utworzona id=${mapId.slice(0, 8)}`);

  // 2. Create seeds
  let totalNodes = 0;
  let totalEntities = 0;
  let totalEav = 0;
  let totalRelations = 0;

  for (let seedIdx = 0; seedIdx < SEED_TEMPLATES.length; seedIdx++) {
    const seedTpl = SEED_TEMPLATES[seedIdx];
    // Payload minimum — matches LLMSeedOutput shape + user_id/map_id/sort_order (hub_* fields use DB defaults)
    const { data: seedRow, error: seedErr } = await (supabase as any)
      .from("thematic_seeds")
      .insert({
        user_id: userId,
        map_id: mapId,
        name: seedTpl.name,
        slug: seedTpl.slug,
        intent_family: ["informational"],
        priority: seedIdx + 1,
        seed_type: seedTpl.seed_type,
        scope_coverage: seedTpl.nodes.map((n) => n.entity.toLowerCase()),
        sort_order: seedIdx,
      })
      .select()
      .single();

    if (seedErr) throw seedErr;
    const seedId = seedRow.id;

    // 3. Create nodes per seed
    const nodeIds: string[] = [];
    for (let i = 0; i < seedTpl.nodes.length; i++) {
      const nodeTpl = seedTpl.nodes[i];
      const slug = `${seedTpl.slug}-${i + 1}-${nodeTpl.entity.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
      const { data: nodeRow, error: nodeErr } = await (supabase as any)
        .from("thematic_nodes")
        .insert({
          user_id: userId,
          map_id: mapId,
          seed_id: seedId,
          title: nodeTpl.title,
          canonical_question: nodeTpl.title,
          slug,
          original_question: nodeTpl.title,
          document_role: nodeTpl.role,
          primary_entity_raw: nodeTpl.entity,
          primary_intent: nodeTpl.role,
          query_frame: nodeTpl.role === "definition" ? "definitional" : nodeTpl.role === "how_to" ? "procedural" : "comparative",
          search_volume: Math.floor(Math.random() * 5000),
          cpc: Math.random() * 3,
          keyword_difficulty: Math.floor(Math.random() * 100),
          demand_tier: ["VM", "WS", "ND", "CV"][Math.floor(Math.random() * 4)],
          publish_priority: i === 0 ? "P1" : "P2",
          sort_order: i,
        })
        .select()
        .single();

      if (nodeErr) throw nodeErr;
      nodeIds.push(nodeRow.id);
      totalNodes++;

      // 4. Create entity for each node
      const { data: entRow, error: entErr } = await (supabase as any)
        .from("thematic_entities")
        .insert({
          user_id: userId,
          map_id: mapId,
          name: nodeTpl.entity,
          aliases: [nodeTpl.entity.toLowerCase()],
          entity_type: "concept",
        })
        .select()
        .single();

      if (entErr && !entErr.message.includes("duplicate")) {
        // Don't throw — entity might already exist from previous nodes (same entity name)
        uiLog.debug("TestGen", `Entity skipped (likely duplicate): ${entErr.message}`);
      }

      if (entRow) {
        totalEntities++;
        // 5. Create EAV attribute
        const { error: eavErr } = await (supabase as any)
          .from("thematic_entity_attributes")
          .insert({
            user_id: userId,
            entity_id: entRow.id,
            map_id: mapId,
            attribute_name: nodeTpl.attribute,
            attribute_value: `Test value for ${nodeTpl.attribute}`,
            tier: "ROOT",
          });
        if (!eavErr) totalEav++;
      }
    }

    // 6. Create relations within seed (linear chain: 1→2→3)
    for (let i = 0; i < nodeIds.length - 1; i++) {
      const { error: relErr } = await (supabase as any)
        .from("thematic_node_relations")
        .insert({
          user_id: userId,
          map_id: mapId,
          from_node_id: nodeIds[i],
          to_node_id: nodeIds[i + 1],
          relation_type: "sibling",
          weight: 5,
          reason: "test sequential relation",
        });
      if (!relErr) totalRelations++;
    }
  }

  uiLog.public(
    "TestGen",
    `Done — seeds=2 nodes=${totalNodes} entities=${totalEntities} eav=${totalEav} relations=${totalRelations}`,
  );

  return {
    mapId,
    mapName,
    seedCount: SEED_TEMPLATES.length,
    nodeCount: totalNodes,
    entityCount: totalEntities,
    eavCount: totalEav,
    relationCount: totalRelations,
  };
}

/**
 * Usuwa wszystkie mapy z prefixem TEST_ (cleanup).
 */
export async function deleteAllTestMaps(): Promise<number> {
  uiLog.public("TestGen", "Czyszczenie map testowych");
  const { data, error } = await (supabase as any)
    .from("thematic_maps")
    .delete()
    .like("name", "TEST_%")
    .select("id");
  if (error) throw error;
  const count = data?.length || 0;
  uiLog.public("TestGen", `Usunięto ${count} map testowych`);
  return count;
}
